import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { socketClient } from "../api/socket";
import {
  calculateTotal,
  clearCart as clearCartDB,
  deleteCartItem,
  getCart,
  putCartItem,
} from "../cart/db";
import { useLocale } from "./LocaleContext";
import type { CartMap, Pixel } from "../types";

interface CartContextValue {
  items: CartMap;
  count: number;
  total: number;
  refresh: () => Promise<void>;
  /** Reserve a pixel on the server and add it to the local cart. */
  reserve: (pixel: Pixel) => Promise<{ ok: boolean; error?: string }>;
  /** Unreserve a pixel on the server and remove it locally. */
  unreserve: (pixel: number) => Promise<{ ok: boolean; error?: string }>;
  clear: () => Promise<void>;
  /** Merge edits into one line item and persist to IndexedDB. */
  patchItem: (pixel: number, updater: (previous: Pixel) => Pixel) => Promise<void>;
}

const CartContext = createContext<CartContextValue | null>(null);

/** Merge server cart pixel with local edits (customer) and keep known address. */
function mergeCartPixel(srv: Pixel, prev?: Pixel): Pixel {
  const merged: Pixel = { ...srv };
  if (prev?.customer && Object.keys(prev.customer).length > 0) {
    merged.customer = prev.customer;
  }
  if (!merged.location && prev?.location) {
    merged.location = prev.location;
  }
  if (!merged.country && prev?.country) {
    merged.country = prev.country;
  }
  return merged;
}

export function CartProvider({ children }: { children: ReactNode }) {
  const { currency } = useLocale();
  const [items, setItems] = useState<CartMap>({});

  /** Drop stale results from overlapping `refresh()` / WS cart sync. */
  const cartHydrateEpochRef = useRef(0);

  const refresh = useCallback(async () => {
    const epoch = (cartHydrateEpochRef.current += 1);
    const data = await getCart();
    if (epoch !== cartHydrateEpochRef.current) return;
    setItems(data);
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // WebSocket handshake sends `pixels_reserved` (Redis cart mirror). Applying it
  // drops pixels the server removed (e.g. after payment) instead of resurrecting stale IDB.
  useEffect(() => {
    const off = socketClient.on<CartMap | Record<string, Pixel>>(
      "pixels_reserved",
      async (msg) => {
        const raw = msg.content;
        const serverMap: CartMap =
          raw &&
          typeof raw === "object" &&
          !Array.isArray(raw) &&
          raw !== null
            ? { ...(raw as CartMap) }
            : {};

        try {
          cartHydrateEpochRef.current += 1;
          const existing = await getCart();
          await clearCartDB();
          const nextMap: CartMap = {};
          for (const id of Object.keys(serverMap)) {
            const srv = serverMap[id];
            if (!srv || typeof srv.pixel !== "number") continue;
            const key = String(srv.pixel);
            nextMap[key] = mergeCartPixel(srv, existing[key]);
            await putCartItem(nextMap[key]);
          }
          setItems(nextMap);
        } catch (e) {
          console.error("pixels_reserved sync failed:", e);
        }
      },
    );
    return off;
  }, []);

  // Server may broadcast cart expirations (keys expire in Redis).
  useEffect(() => {
    const off = socketClient.on("cart_expired", async () => {
      cartHydrateEpochRef.current += 1;
      await clearCartDB();
      setItems({});
    });
    return off;
  }, []);

  const reserve = useCallback(
    async (pixel: Pixel) => {
      try {
        const content = await socketClient.awaitLegacyResponse<{
          success: boolean;
          pixels?: CartMap;
          error?: string;
        }>("reserve", { data: pixel.pixel });
        if (content?.pixels) {
          cartHydrateEpochRef.current += 1;
          const existing = await getCart();
          await clearCartDB();
          const nextMap: CartMap = {};
          for (const id in content.pixels) {
            const srv = content.pixels[id];
            nextMap[id] = mergeCartPixel(srv, existing[id]);
            await putCartItem(nextMap[id]);
          }
          setItems(nextMap);
        } else if (content?.success !== false) {
          await putCartItem(pixel);
          await refresh();
        }
        return { ok: !!content?.success, error: content?.error };
      } catch (err) {
        return { ok: false, error: (err as Error).message };
      }
    },
    [refresh],
  );

  const unreserve = useCallback(
    async (pixel: number) => {
      const key = String(pixel);
      try {
        const existing = await getCart();
        await deleteCartItem(pixel);
        setItems((prev) => {
          if (!prev[key]) return prev;
          const next = { ...prev };
          delete next[key];
          return next;
        });
        const content = await socketClient.awaitLegacyResponse<{
          success: boolean;
          pixels?: CartMap;
          error?: string;
        }>("unreserve", { data: pixel });
        if (content?.pixels) {
          cartHydrateEpochRef.current += 1;
          await clearCartDB();
          const nextMap: CartMap = {};
          for (const id in content.pixels) {
            const srv = content.pixels[id];
            nextMap[id] = mergeCartPixel(srv, existing[id]);
            await putCartItem(nextMap[id]);
          }
          setItems(nextMap);
        } else {
          await refresh();
        }
        return { ok: !!content?.success, error: content?.error };
      } catch (err) {
        await refresh();
        return { ok: false, error: (err as Error).message };
      }
    },
    [refresh],
  );

  const clear = useCallback(async () => {
    cartHydrateEpochRef.current += 1;
    await clearCartDB();
    setItems({});
  }, []);

  const patchItem = useCallback(
    async (pixel: number, updater: (previous: Pixel) => Pixel) => {
      const key = String(pixel);
      let next: Pixel | null = null;
      setItems((prev) => {
        const cur = prev[key];
        if (!cur) return prev;
        const updated = updater(cur);
        next = updated;
        return { ...prev, [key]: updated };
      });
      if (next) await putCartItem(next);
    },
    [],
  );

  const value = useMemo<CartContextValue>(() => {
    const count = Object.keys(items).length;
    return {
      items,
      count,
      total: calculateTotal(items, currency),
      refresh,
      reserve,
      unreserve,
      clear,
      patchItem,
    };
  }, [items, currency, refresh, reserve, unreserve, clear, patchItem]);

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart(): CartContextValue {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used inside <CartProvider>");
  return ctx;
}
